import type React from "react";
import { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import type { AppDispatch, RootState } from "../store";
import { fetchCities } from "../store/features/citySlice";
import { searchRetiredTrucks } from "../store/features/retiredTruckSlice";
import TruckTable from "../components/trucks/TruckTable";
import CreateTruckForm from "../components/trucks/CreateTruckForm";
import { useTranslation } from "react-i18next";
import type translations from "../translations/translations.json";
import type { TruckSearchFilters } from "../types/truck";
import { createTruckApi } from "../api/trucksApi";
import { getErrorMessage } from "../utils/errorHandling";
import { toastEmitter } from "../utils/toastEmitter";
import type { PaginationParam } from "../types/paginationResponse";
import { isUserAdmin } from "../types/user";
import {
  selectRetiredTrucks,
  selectRetiredTrucksLoading,
  selectRetiredTrucksError,
  selectRetiredTrucksPagination,
} from "../store/features/retiredTruckSlice";

type TranslationKeys = keyof typeof translations.en;

const RetiredTrucksPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.user);
  const { trucks, loading, error, pagination } = useSelector((state: RootState) => ({
    trucks: selectRetiredTrucks(state),
    loading: selectRetiredTrucksLoading(state),
    error: selectRetiredTrucksError(state),
    pagination: selectRetiredTrucksPagination(state),
  }));
  const { cities } = useSelector((state: RootState) => state.cities);
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const isAdmin = isUserAdmin(user);

  // Form state
  const [open, setOpen] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Search state - initialize once from URL params
  const [filters, setFilters] = useState<TruckSearchFilters>({
    licensePlate: searchParams.get("licensePlate") || "",
    status: searchParams.get("status") || "",
    cityId: searchParams.get("cityId") || "",
    sortBy: searchParams.get("sortBy") || "",
    sortOrder: (searchParams.get("sortOrder") as "asc" | "desc") || "asc",
  });

  const [paginationState, setPaginationState] = useState<PaginationParam>({
    page: Number(searchParams.get("page")) || 1,
    pageSize: Number(searchParams.get("pageSize")) || 10,
  });

  // Initial data loading
  useEffect(() => {
    dispatch(fetchCities());
  }, [dispatch]);

  // Handle URL params and search
  useEffect(() => {
    dispatch(searchRetiredTrucks({ filters, pagination: paginationState }));
  }, [dispatch, filters, paginationState]);

  // Form handlers
  const handleOpen = () => {
    setOpen(true);
    setAddError(null);
  };

  const handleClose = () => {
    setOpen(false);
    setAddError(null);
  };

  const handleSubmit = async (truckData: {
    licensePlate: string;
    type: string;
  }) => {
    try {
      await createTruckApi(truckData);
      await dispatch(
        searchRetiredTrucks({ filters: {}, pagination: { page: 1, pageSize: 10 } }),
      ).unwrap();
      handleClose();
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, "createTruck");
      setAddError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    }
  };

  // Table handlers
  const handlePaginationChange = (newPagination: PaginationParam) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", newPagination.page.toString());
    newParams.set("pageSize", newPagination.pageSize.toString());
    setSearchParams(newParams);
    setPaginationState(newPagination);
  };

  const handleSearch = async (newFilters: TruckSearchFilters, newPagination: PaginationParam) => {
    const newParams = new URLSearchParams();
    for (const [key, value] of Object.entries(newFilters)) {
      if (value) {
        newParams.set(key, value.toString());
      }
    }
    newParams.set("page", newPagination.page.toString());
    newParams.set("pageSize", newPagination.pageSize.toString());
    setSearchParams(newParams);
    setFilters(newFilters);
    setPaginationState(newPagination);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">{t("Retired Trucks" as TranslationKeys)}</Typography>
      </Box>

      <TruckTable
        trucks={trucks}
        cities={cities}
        onSearch={handleSearch}
        pagination={pagination}
        onPaginationChange={handlePaginationChange}
      />

      <CreateTruckForm open={open} onClose={handleClose} onSubmit={handleSubmit} error={addError} />
    </Box>
  );
};

export default RetiredTrucksPage;
